import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { ParsedUrlQueryInput } from "querystring";

import useTripsQuery, { Trip, TripStatus } from "@/fetch/gworld/useTripsQuery";
import { Division } from "@/fetch/gworld";
import { useAuth0 } from "@auth0/auth0-react";
import useCurrentTrip from "./useCurrentTrip";
import useBrowserSettings from "./useBrowserSettings";

const useSetTrip = () => {
  const {
    data: trips,
    isLoading: isTripsLoading,
    isError: hasTripQueryError,
    refetch: refetchTrips,
    error: tripQueryError,
  } = useTripsQuery();

  const [tripRetryCount, setTripRetryCount] = useState(0);

  const { currentTrip, setCurrentTrip } = useCurrentTrip();
  const { logout: auth0Logout } = useAuth0();
  const router = useRouter();

  const { isGapp } = useBrowserSettings();

  const isTripLockedForUnderAgeWaitingApproval = (
    trip: Trip | undefined | null
  ) => {
    // since parent information now filled through gWorld, we want traveller to first
    // fill the parent info and then lock them, so if personal details are not filled yet
    // we let them to continue the setup process and then lock them

    const isLockedForUnderAgeWaitingApproval =
      trip?.status === TripStatus.UnderAgeWaitingApproval; // remove is profileSetup from here, since we redirect user to setup if it does not set up
    return isLockedForUnderAgeWaitingApproval;
  };

  const isTripLocked = (trip: Trip | undefined | null) => {
    if (!trip) return true;

    const lockedTripsStatus = [
      TripStatus.Cancelled,
      TripStatus.OnHold,
      TripStatus.OnHoldFromSales,
      TripStatus.TripCancelled,
      TripStatus.Transfer,
    ];

    const isLockedForUnderAgeWaitingApproval =
      isTripLockedForUnderAgeWaitingApproval(trip);

    return (
      (trip?.status &&
        (lockedTripsStatus.includes(trip.status) ||
          isLockedForUnderAgeWaitingApproval)) ||
      false
    );
  };

  const focusOnNextWorkingTrip = () => {
    const notFitTrips = trips?.filter(
      (trip: Trip) => trip.division !== Division.FIT
    );

    const trip = notFitTrips?.find(
      (trip: Trip) =>
        !isTripLocked(trip) && trip.status !== TripStatus.Cancelling
    );

    if (trip) {
      setCurrentTrip(trip);
    } else {
      setCurrentTrip(notFitTrips?.[0]);
    }
  };

  useEffect(() => {
    if (currentTrip?.division === Division.FIT) {
      focusOnNextWorkingTrip();
    }
  }, [currentTrip]);

  useEffect(() => {
    const getTripToFocus = (): Trip | undefined => {
      const tripIdQueryParam =
        Number((router.query as ParsedUrlQueryInput)?.tripId ?? 0) || null;
      const tripFromParam =
        tripIdQueryParam &&
        trips?.find((trip: Trip) => trip.id === tripIdQueryParam);

      if (tripFromParam) {
        return tripFromParam;
      }

      const selectedTrip =
        currentTrip && trips?.find((trip: Trip) => trip.id === currentTrip.id);

      if (
        selectedTrip &&
        !isTripLocked(selectedTrip) &&
        selectedTrip.division !== Division.FIT
      ) {
        return selectedTrip;
      }

      // If there is no previously selected trip or the selected trip is locked,
      // then then return first trip that is not locked
      const trip = trips?.find(
        (trip: Trip) => !isTripLocked(trip) && trip.division !== Division.FIT
      );
      if (trip) {
        return trip;
      }

      // Otherwise return first trip
      return trips?.[0];
    };

    if (trips?.length) {
      const nextTripToBeSelected = getTripToFocus();
      setCurrentTrip(nextTripToBeSelected);
    }
  }, [trips, currentTrip, setCurrentTrip, router.query]);

  useEffect(() => {
    if (hasTripQueryError && !isTripsLoading) {
      const websiteUrl = "https://globalworkandtravel.com";
      console.log("logging out...");
      if (isGapp) {
        (window as any)?.ReactNativeWebView?.postMessage?.(
          "trip-query-error" + JSON.stringify(tripQueryError)
        );
        if (tripRetryCount > 3) {
          router.push("/logout");
        } else {
          setTripRetryCount(tripRetryCount + 1);
          refetchTrips();
        }
      } else {
        auth0Logout({
          logoutParams: {
            returnTo: isGapp ? websiteUrl : global.window?.location.origin,
          },
        });
      }
    }
  }, [auth0Logout, hasTripQueryError, isTripsLoading]);

  return {
    focusOnNextWorkingTrip,
    isLoading: (isTripsLoading && !currentTrip) || !currentTrip,
    isTripLocked,
  };
};

export default useSetTrip;
